import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { api } from '@/lib/api-client';

export interface FXRatesMetadata {
  rates: Record<string, number> | null;
  base: string;
  lastUpdated: number | null;
  error: string | null;
}

export function useFxRates() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['fx-rates'],
    queryFn: () => api<FXRatesMetadata>('/api/fx-rates'),
    staleTime: 5 * 60 * 1000,
    refetchInterval: 6 * 60 * 60 * 1000,
  });

  const lastUpdated = useMemo(() => {
    if (!data?.lastUpdated) return null;
    return new Date(data.lastUpdated).toLocaleString();
  }, [data?.lastUpdated]);

  const lastUpdatedRelative = useMemo(() => {
    if (!data?.lastUpdated) return 'Never';
    const diffMs = Date.now() - data.lastUpdated;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return new Date(data.lastUpdated).toLocaleDateString();
  }, [data?.lastUpdated]);

  return {
    rates: data?.rates || null,
    base: data?.base || 'USD',
    lastUpdated: data?.lastUpdated,
    lastUpdatedFormatted: lastUpdated,
    lastUpdatedRelative,
    error: data?.error || error?.message,
    isLoading,
    refetch,
  };
}